import { createPluginRegistration } from "@embedpdf/core";
import type { PluginBatchRegistrations } from "@embedpdf/core";
import { DocumentManagerPluginPackage } from "@embedpdf/plugin-document-manager";
import { InteractionManagerPluginPackage } from "@embedpdf/plugin-interaction-manager";
import { RenderPluginPackage } from "@embedpdf/plugin-render";
import { RotatePluginPackage } from "@embedpdf/plugin-rotate";
import { ScrollPluginPackage } from "@embedpdf/plugin-scroll";
import { SearchPluginPackage } from "@embedpdf/plugin-search";
import { SelectionPluginPackage } from "@embedpdf/plugin-selection";
import { ThumbnailPluginPackage } from "@embedpdf/plugin-thumbnail";
import { TilingPluginPackage } from "@embedpdf/plugin-tiling";
import { ViewportPluginPackage } from "@embedpdf/plugin-viewport";
import { ZoomMode, ZoomPluginPackage } from "@embedpdf/plugin-zoom";

import type { PdfViewerZoom } from "./PdfViewer.types";
import {
  PDF_VIEWER_PAGE_GAP,
  PDF_VIEWER_THUMBNAIL_BUFFER,
  PDF_VIEWER_THUMBNAIL_GAP,
  PDF_VIEWER_THUMBNAIL_LABEL_HEIGHT,
  PDF_VIEWER_THUMBNAIL_WIDTH,
  PDF_VIEWER_TILE_SIZE,
  PDF_VIEWER_VIEWPORT_GAP,
} from "./constants";

const ZOOM_MODES = {
  automatic: ZoomMode.Automatic,
  "fit-page": ZoomMode.FitPage,
  "fit-width": ZoomMode.FitWidth,
} as const;

export function toZoomLevel(zoom: PdfViewerZoom) {
  return typeof zoom === "number" ? zoom : ZOOM_MODES[zoom];
}

export type PdfViewerPluginOptions = {
  documentId: string;
  src: string;
  name: string;
  password?: string;
  zoom: PdfViewerZoom;
};

export function createPdfViewerPlugins({
  documentId,
  src,
  name,
  password,
  zoom,
}: PdfViewerPluginOptions): PluginBatchRegistrations {
  return [
    createPluginRegistration(DocumentManagerPluginPackage, {
      initialDocuments: [{ url: src, documentId, name, password }],
    }),
    createPluginRegistration(ViewportPluginPackage, {
      viewportGap: PDF_VIEWER_VIEWPORT_GAP,
    }),
    createPluginRegistration(ScrollPluginPackage, {
      defaultPageGap: PDF_VIEWER_PAGE_GAP,
    }),
    // Both default to false, which leaves a form's fields and a document's
    // annotations undrawn: the page renders, minus part of its content.
    createPluginRegistration(RenderPluginPackage, {
      withForms: true,
      withAnnotations: true,
    }),
    createPluginRegistration(TilingPluginPackage, {
      tileSize: PDF_VIEWER_TILE_SIZE,
      overlapPx: 2.5,
      extraRings: 0,
    }),
    createPluginRegistration(ZoomPluginPackage, {
      defaultZoomLevel: toZoomLevel(zoom),
    }),
    createPluginRegistration(RotatePluginPackage),
    createPluginRegistration(InteractionManagerPluginPackage),
    createPluginRegistration(SelectionPluginPackage),
    createPluginRegistration(SearchPluginPackage),
    createPluginRegistration(ThumbnailPluginPackage, {
      width: PDF_VIEWER_THUMBNAIL_WIDTH,
      gap: PDF_VIEWER_THUMBNAIL_GAP,
      labelHeight: PDF_VIEWER_THUMBNAIL_LABEL_HEIGHT,
      buffer: PDF_VIEWER_THUMBNAIL_BUFFER,
    }),
  ];
}
